import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Skeleton,
    Fade,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    MoreVert as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Assessment as AssessmentIcon,
    Launch as LaunchIcon,
    ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import { useNotification } from '../../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import Breadcrumb from '../common/Breadcrumb';
import PageLayout from '../common/PageLayout';
import DeleteIntegrationConfirmModal from './DeleteIntegrationConfirmModal';
import ResponsiveTooltip from '../ResponsiveTooltip';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useIntegrationService } from '../../services/integration.service';
import { formatCurrency } from '../../utils/formatters';
import { QUERY_KEYS } from '../../utils/queryKeys';

const IntegrationDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { integrationId } = useParams<{ integrationId: string }>();
    const { integrations, syncIntegration } = useIntegrationsStatusContext();
    const { showNotification } = useNotification();
    const { currentCtUser } = useAuth();
    const integrationService = useIntegrationService();
    const theme = useTheme();
    const assetsRef = useRef<HTMLDivElement>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Find the integration by ID (using name as ID for now)
    const integration = integrations?.find(i => i.name === integrationId);

    // Fetch token allocation data
    const { data: tokenAllocation, isLoading: isLoadingAssets } = useQuery({
        queryKey: [QUERY_KEYS.INTEGRATIONS_TOKEN_ALLOCATION, integrationId],
        queryFn: () => integrationService.getTokenAllocation(integrationId!),
        enabled: !!currentCtUser && !!integrationId,
    });

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSync = () => {
        if (!integration) return;
        
        syncIntegration(
            { 
                jobId: integration.jobId, 
                platformName: integration.platformName,
                name: integration.name
            },
            {
                onSuccess: () => {
                    showNotification(t('integrations.syncStarted'), 'success');
                },
                onError: () => {
                    showNotification(t('integrations.syncError'), 'error');
                },
            }
        );
        handleMenuClose();
    };

    const handleDelete = () => {
        setShowDeleteModal(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = () => {
        if (!integration) return;
        // TODO: Implement delete logic using useIntegrationMutations
        setShowDeleteModal(false);
        navigate('/integrations');
    };

    const handleViewAssets = () => {
        assetsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        handleMenuClose();
    };

    const handleNavigateToTransactions = (assetSymbol: string, accountName: string) => {
        navigate(`/transactions?account=${accountName}&asset=${assetSymbol}`);
    };

    const breadcrumbItems = [
        { label: t('integrations.title'), path: '/integrations' },
        { label: integration?.name || integrationId || '' }
    ];

    if (!integration) {
        return (
            <PageLayout>
                <Breadcrumb items={breadcrumbItems} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Typography variant="h6">
                        {t('integrations.notFound')}
                    </Typography>
                </Box>
            </PageLayout>
        );
    }

    const getStatusIcon = () => {
        if (integration.status.status === 'in_progress') {
            return <RefreshIcon sx={{ animation: 'spin 2s linear infinite' }} />;
        } else if (integration.status.status === 'error') {
            return <ErrorIcon color="error" />;
        }
        return <CheckCircleIcon color="success" />;
    };

    return (
        <PageLayout>
            <Breadcrumb items={breadcrumbItems} />

            {/* Asset Allocation Section */}
            <Paper sx={{ p: 3 }} ref={assetsRef}>
                {isLoadingAssets ? (
                    <Fade in={isLoadingAssets} timeout={300}>
                        <Box>
                            <TableContainer>
                                <Table size="medium" sx={{
                                    minWidth: 750,
                                    tableLayout: 'fixed',
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        borderBottom: '2px solid',
                                        borderBottomColor: `${theme.palette.divider}`,
                                        borderBottomOpacity: 0.6,
                                    },
                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                        bgcolor: 'background.paper',
                                        fontWeight: 600,
                                        borderBottom: '2px solid',
                                        borderBottomColor: `${theme.palette.divider}`,
                                    },
                                }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.asset')}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.quantity')}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.value')}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.allocation')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[1, 2, 3, 4, 5, 6].map((rowIndex) => (
                                            <TableRow key={`skeleton-row-${rowIndex}`}>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Skeleton variant="circular" width={24} height={24}
                                                                sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1) }} />
                                                            <Box>
                                                                <Skeleton width={80} height={20}
                                                                    sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1) }} />
                                                                <Skeleton width={40} height={14}
                                                                    sx={{
                                                                        bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                                        mt: 0.5
                                                                    }} />
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Skeleton width={60} height={20}
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                            ml: 'auto'
                                                        }} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Skeleton width={70} height={20}
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                            ml: 'auto'
                                                        }} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Skeleton width={50} height={20}
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                            ml: 'auto'
                                                        }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Fade>
                ) : tokenAllocation && (
                    <Fade in={!isLoadingAssets} timeout={500}>
                        <Box>
                            {(() => {
                                const accountsWithAssets = Object.entries(tokenAllocation).filter(([_, assets]) => assets.length > 0);
                                const showAccountNames = accountsWithAssets.length > 1;

                                return accountsWithAssets.map(([key, assets]) => (
                                    <Box key={key} sx={{ mb: 3 }}>
                                        {showAccountNames && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {key}
                                                </Typography>
                                            </Box>
                                        )}
                                        <TableContainer
                                            sx={{
                                                overflow: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    height: 6,
                                                    width: '8px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: 'transparent',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: alpha(theme.palette.divider, 0.6),
                                                    borderRadius: 3,
                                                },
                                                '&::-webkit-scrollbar-thumb:hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                },
                                            }}
                                        >
                                            <Table
                                                size="medium"
                                                sx={{
                                                    minWidth: { xs: 600, sm: 600, md: 750 },
                                                    tableLayout: 'fixed',
                                                    borderCollapse: 'collapse',
                                                    width: '100%',
                                                    '& .MuiTableCell-root': {
                                                        py: 1.5,
                                                        px: { xs: 1, sm: 2 },
                                                        borderBottom: '2px solid',
                                                        borderBottomColor: `${theme.palette.divider}`,
                                                        borderBottomOpacity: 0.6,
                                                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    },
                                                    '& .MuiTypography-root': {
                                                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                                    },
                                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                                        bgcolor: 'background.paper',
                                                        fontWeight: 600,
                                                        borderBottom: '2px solid',
                                                        borderBottomColor: `${theme.palette.divider}`,
                                                    },
                                                    '& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
                                                        borderBottom: 'none',
                                                    },
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: { xs: '40%', sm: '45%', md: '50%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.asset')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '20%', md: '20%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.quantity')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '20%', md: '15%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.value')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '15%', md: '15%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.allocation')}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {assets.map((asset) => (
                                                        <TableRow key={`${key}-${asset.coin}`}>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1
                                                                        }}
                                                                    >
                                                                        <Avatar
                                                                            src={`https://cointracking.info/img/coins/${asset.image}`}
                                                                            alt={asset.coin || ''}
                                                                            sx={{ width: 24, height: 24 }}
                                                                        />
                                                                        <Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    fontWeight: 'bold',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                                                    maxWidth: { xs: '120px', sm: '150px', md: '200px', lg: '250px' }
                                                                                }}
                                                                            >
                                                                                <ResponsiveTooltip title={asset.name || asset.coin}>
                                                                                    <span>{asset.name || asset.coin}</span>
                                                                                </ResponsiveTooltip>
                                                                                {asset.quantity_mismatch ? (
                                                                                    <>
                                                                                        <ResponsiveTooltip
                                                                                            title={t('dashboard.assetDistribution.balanceDiscrepancy.tooltip', {
                                                                                                balance: asset.imported_quantity.toFixed(8)
                                                                                            })}
                                                                                            arrow
                                                                                            placement="top"
                                                                                        >
                                                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                                                                                                <ReportProblemIcon
                                                                                                    fontSize="small"
                                                                                                    color="warning"
                                                                                                    sx={{ cursor: 'default' }}
                                                                                                />
                                                                                            </Box>
                                                                                        </ResponsiveTooltip>
                                                                                        <ResponsiveTooltip
                                                                                            title={t('integrations.assetsModal.reconcileTransactions')}
                                                                                            arrow
                                                                                            placement="top"
                                                                                        >
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                color="primary"
                                                                                                onClick={() => handleNavigateToTransactions(asset.coin, key)}
                                                                                                sx={{
                                                                                                    padding: '2px',
                                                                                                    ml: 0.5,
                                                                                                    '&:hover': {
                                                                                                        backgroundColor: theme.palette.primary.light + '20',
                                                                                                    },
                                                                                                }}
                                                                                            >
                                                                                                <LaunchIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </ResponsiveTooltip>
                                                                                    </>
                                                                                ) : (
                                                                                    ''
                                                                                )}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {asset.symbol && asset.symbol.trim() ? (
                                                                                    <span
                                                                                        dangerouslySetInnerHTML={{
                                                                                            __html: asset.symbol,
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    asset.coin
                                                                                )}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <ResponsiveTooltip title={asset.quantity.toFixed(8)}>
                                                                    <Typography variant="body2" sx={{
                                                                        fontWeight: 'bold',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {asset.quantity.toFixed(8)}
                                                                    </Typography>
                                                                </ResponsiveTooltip>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <ResponsiveTooltip title={formatCurrency(asset.value)}>
                                                                    <Typography variant="body2" sx={{
                                                                        fontWeight: 'bold',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {formatCurrency(asset.value)}
                                                                    </Typography>
                                                                </ResponsiveTooltip>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2" sx={{
                                                                    fontWeight: 'bold',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {asset.allocation.toFixed(2)}%
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ));
                            })()}
                        </Box>
                    </Fade>
                )}
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewAssets}>
                    <ListItemIcon>
                        <AssessmentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('integrations.viewAssets')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('integrations.delete')}</ListItemText>
                </MenuItem>
            </Menu>

            {showDeleteModal && integration && (
                <DeleteIntegrationConfirmModal
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteConfirm}
                    integration={integration}
                    isDeleting={false}
                />
            )}
        </PageLayout>
    );
};

export default IntegrationDetailPage;